import { useEffect, useState } from "react";
import { Box, Button, Chip, Typography } from "@mui/material";
import CallMadeIcon from "@mui/icons-material/CallMade";
import CallReceivedIcon from "@mui/icons-material/CallReceived";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { dataGridStyle, colors } from "../../../../theme";
import API from "../../../../api";

const DIRECTION_STYLE = {
  Both: { color: "#68BBBC", icon: SyncAltIcon, label: "Bidirectional" },
  Source: { color: "#CBBA54", icon: CallMadeIcon, label: "Outbound" },
  Destination: { color: "#c14f60", icon: CallReceivedIcon, label: "Inbound" },
};

// Source↔destination pair table driven by security-log data (tiresia
// getIpDetail) — ported from the removed "Search by IP" page's table.jsx.
// Distinct from the Connections tab's conduit table: rows here are aggregated
// log counts per pair, with a "Details" drill-down into the full log grid.
const TrafficLogsTable = ({ ipAddress, dateFilter, multiSecurityView = false, onOpenPair, loading, setLoading, setGraphData }) => {
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });
  const [data, setData] = useState([]);
  const [rowCounter, setRowCounter] = useState(0);

  useEffect(() => {
    if (!ipAddress) return;
    setLoading(true);
    const abortController = new AbortController();
    const apiCall = multiSecurityView ? API.getIpDetailAllLecs : API.getIpDetail;
    apiCall({
      ip: ipAddress,
      pageNumber: paginationModel.page,
      limit: paginationModel.pageSize,
      dateFrom: dateFilter?.dateFrom?.toISOString?.() ?? dateFilter?.dateFrom,
      dateTo: dateFilter?.dateTo?.toISOString?.() ?? dateFilter?.dateTo,
    }, abortController.signal)
      .then((result) => {
        setData((result.data.data || []).map((item, index) => ({ id: index, ...item })));
        setGraphData(result.data.data || []);
        setRowCounter(result.data.totalLogs || 0);
        setLoading(false);
      })
      .catch((error) => {
        console.error("[TrafficLogsTable] Error fetching pairs:", error);
        setLoading(false);
      });
    return () => abortController.abort();
  }, [ipAddress, paginationModel.page, paginationModel.pageSize, dateFilter, multiSecurityView, setLoading, setGraphData]);

  const handlePaginationModelChange = (model) => {
    if (model.page > paginationModel.page) setPaginationModel({ ...paginationModel, page: paginationModel.page + 1 });
    else if (model.page < paginationModel.page) setPaginationModel({ ...paginationModel, page: paginationModel.page - 1 });
    else setPaginationModel(model);
  };

  const columns = [
    {
      field: "sourceIp",
      headerName: "SOURCE",
      flex: 1,
      minWidth: 140,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => (
        <Typography sx={{ fontFamily: "monospace", fontSize: 12.5, color: params.value === ipAddress ? "#459095" : colors.grey[100], fontWeight: params.value === ipAddress ? 700 : 400 }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: "destIp",
      headerName: "DESTINATION",
      flex: 1,
      minWidth: 140,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => (
        <Typography sx={{ fontFamily: "monospace", fontSize: 12.5, color: params.value === ipAddress ? "#459095" : colors.grey[100], fontWeight: params.value === ipAddress ? 700 : 400 }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: "type",
      headerName: "DIRECTION",
      flex: 0.8,
      minWidth: 150,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => {
        const style = DIRECTION_STYLE[params.value] ?? { color: colors.grey[400], icon: SyncAltIcon, label: params.value };
        const Icon = style.icon;
        return (
          <Chip
            icon={<Icon sx={{ fontSize: "14px !important" }} />}
            label={style.label}
            size="small"
            sx={{ bgcolor: `${style.color}22`, color: style.color, fontWeight: 600, fontSize: 11, height: 22, "& .MuiChip-icon": { color: style.color } }}
          />
        );
      },
    },
    {
      field: "count",
      headerName: "COUNTS",
      flex: 0.5,
      minWidth: 90,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => (
        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: colors.grey[100] }}>{params.value ?? "—"}</Typography>
      ),
    },
    {
      field: "details",
      headerName: "",
      flex: 0.6,
      minWidth: 110,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          size="small"
          onClick={() => onOpenPair({ ipSource: params.row.sourceIp, ipDest: params.row.destIp })}
          endIcon={<ChevronRightIcon sx={{ fontSize: "16px !important", ml: -0.4 }} />}
          sx={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 0.3,
            textTransform: "none",
            borderRadius: 999,
            px: 1.4,
            py: 0.35,
            minWidth: 0,
            color: "#459095",
            bgcolor: "#45909518",
            border: "1px solid #45909545",
            transition: "background-color 150ms, border-color 150ms, transform 150ms",
            "&:hover": { bgcolor: "#45909533", borderColor: "#459095", transform: "translateX(1px)" },
          }}
        >
          Details
        </Button>
      ),
    },
  ];

  return (
    <Box sx={{ height: "100%" }}>
      <DataGridPro
        rows={data}
        columns={columns}
        paginationMode="server"
        pageSizeOptions={[25, 50, 100]}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        rowCount={rowCounter}
        loading={loading}
        getRowHeight={() => "auto"}
        sx={dataGridStyle}
        slotProps={{ loadingOverlay: { variant: "skeleton" } }}
      />
    </Box>
  );
};

export default TrafficLogsTable;
